import { observer } from 'mobx-react-lite'
import type { PersonalInfoEditorViewModel } from '@/features/tax-profile/individual-profiles/PersonalInfoEditorViewModel'
import { TextFieldContainer } from '@/components/inputs/TextFieldContainer'
import { FormFieldGroup } from '@/components/Form'
import { StyledLink } from '@/components/Link'
import {
  ModalDialog,
  ModalDialogActions,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { Button } from '@/components/Button'
import { useState } from 'react'
import { DateFieldContainer } from '@/components/inputs/DateFieldContainer'
import { masks } from '@/utils/input-mask-util'

export interface PersonalInfoEditorProps {
  vm: PersonalInfoEditorViewModel
  privacyDialogTestId?: string
  privacyDialogCloseButtonTestId?: string
  testId?: string
}

/**
 * Form fields for personal information.
 */
export const PersonalInfoEditor = observer(function PersonalInfoEditor({
  vm,
  privacyDialogTestId,
  privacyDialogCloseButtonTestId,
  testId,
}: PersonalInfoEditorProps) {
  const [isPrivacyDialogOpen, setIsPrivacyDialogOpen] = useState(false)
  return (
    <>
      <FormFieldGroup stackWhenCompact>
        <TextFieldContainer
          model={vm}
          field="firstName"
          placeholder="e.g. John"
          label="First name"
          testId={testId ? `${testId}-first-name-input` : undefined}
        />
        <TextFieldContainer
          model={vm}
          field="middleInitial"
          label="Middle initial"
          placeholder="e.g. R"
          testId={testId ? `${testId}-middle-initial-input` : undefined}
        />
      </FormFieldGroup>
      <TextFieldContainer
        model={vm}
        field="lastName"
        placeholder="e.g. Doe"
        label="Last name"
        testId={testId ? `${testId}-last-name-input` : undefined}
      />
      <DateFieldContainer
        model={vm}
        field="dateOfBirth"
        placeholder="MM/DD/YYYY e.g. 04/28/1998"
        label="Date of birth"
        data-private
        testId={testId ? `${testId}-date-of-birth-input` : undefined}
      />
      <TextFieldContainer
        model={vm}
        field="personalTaxpayerIdentificationNumber"
        placeholder="e.g. 123-45-6789"
        label="SSN or ITIN"
        mask={masks.ssnOrItin}
        data-private
        testId={testId ? `${testId}-ssn-or-itin-input` : undefined}
        hint={
          <>
            <StyledLink
              variant="inherit"
              onPress={() => setIsPrivacyDialogOpen(true)}
              testId={testId ? `${testId}-privacy-link` : undefined}
            >
              We take your privacy seriously
            </StyledLink>
          </>
        }
      />
      <PrivacyInfoDialog
        isOpen={isPrivacyDialogOpen}
        onClose={() => setIsPrivacyDialogOpen(false)}
        testId={privacyDialogTestId}
        closeButtonTestId={privacyDialogCloseButtonTestId}
      />
    </>
  )
})

interface PrivacyInfoDialogProps {
  isOpen: boolean
  onClose: () => unknown
  testId?: string
  closeButtonTestId?: string
}

/**
 * Privacy Info Dialog
 */
export function PrivacyInfoDialog({
  isOpen,
  onClose,
  testId,
  closeButtonTestId,
}: PrivacyInfoDialogProps) {
  return (
    <ModalDialog isOpen={isOpen} onClose={onClose} size="lg" testId={testId}>
      <ModalDialogHeader>
        <ModalDialogTitle>We take your privacy seriously</ModalDialogTitle>
        <ModalDialogDescription>
          <p>
            We wouldn't ask for your social security number or individual
            taxpayer identification number if they weren't required to file your
            tax return. This information is essential to file a return and claim
            possible credits.
          </p>
          <p>
            We are annually tested to confirm we meet SOC 2 Type II and ISO
            27001 compliance requirements. These compliance audits are performed
            by external auditors to review and examine our information security
            control measures and operations.
          </p>
        </ModalDialogDescription>
      </ModalDialogHeader>
      <ModalDialogFooter>
        <ModalDialogActions>
          <Button
            variant="outline"
            label="Close"
            onPress={onClose}
            testId={closeButtonTestId}
          />
        </ModalDialogActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
}
